import { StateCreator } from "zustand";
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue } from "antd/es/table/interface";

import { MediaProject } from "pages/media/types";
import { sliceResetFns } from "store/utils";
import { CombinedSlice } from "store/types";
import {
  ProjectTableViewMode,
  ProjectStatusFilterKey,
  ProjectTypeFilterKey,
} from "./types";
import {
  DEFAULT_VISIBLE_PROJECT_FIELD_COLUMNS,
  ProjectFieldColumnKey,
} from "helpers/table/projectFieldColumns";

type SanitizedSorter<T> = {
  field: keyof T | readonly (keyof T)[];
  order: "ascend" | "descend" | null;
  columnKey: string;
};

export interface ProjectsSlice<T = MediaProject> {
  setProjectsState: (state: Partial<ProjectsState<MediaProject>>) => void;
  resetProjectsState: () => void;
  projectsState: ProjectsState<T>;
}

interface ProjectsState<T> {
  pagination: TablePaginationConfig;
  filters: Record<string, FilterValue | null>;
  sorter: SanitizedSorter<T> | SanitizedSorter<T>[];
  searchText: string;
  viewMode: ProjectTableViewMode;
  columnOrder: string[];
  visibleFieldColumns: ProjectFieldColumnKey[];
  statusFilter: ProjectStatusFilterKey;
  typeFilter: ProjectTypeFilterKey | null;
}

// Keys of the 4 optional analytics field columns, in canonical order — the
// slot the old single "analytics" group column used to occupy.
export const DEFAULT_ANALYTICS_COLUMN_ORDER: ProjectFieldColumnKey[] = [
  "views",
  "videoReproductions",
  "documentInteractions",
  "chatConversations",
];

export const DEFAULT_PROJECT_COLUMN_ORDER: string[] = [
  "title",
  "created",
  "updated",
  "type",
  ...DEFAULT_ANALYTICS_COLUMN_ORDER,
  "tags",
  "status",
  "organization_id",
];

const initialState: ProjectsState<MediaProject> = {
  pagination: { current: 1, pageSize: 50 },
  filters: {},
  sorter: { field: "" as keyof MediaProject, order: null, columnKey: "" },
  searchText: "",
  viewMode: "table" as ProjectTableViewMode,
  columnOrder: DEFAULT_PROJECT_COLUMN_ORDER,
  visibleFieldColumns: DEFAULT_VISIBLE_PROJECT_FIELD_COLUMNS,
  statusFilter: "all",
  typeFilter: null,
};

export const createProjectsSlice: StateCreator<
  CombinedSlice,
  //https://github.com/pmndrs/zustand/issues/980#issuecomment-1162289836
  [
    ["zustand/devtools", never],
    ["zustand/persist", unknown],
    ["zustand/immer", never],
  ],
  [],
  ProjectsSlice
> = (set) => {
  sliceResetFns.add(() => set({ projectsState: initialState }));
  return {
    projectsState: initialState,
    resetProjectsState: () => set({ projectsState: initialState }),
    setProjectsState: (newState) =>
      set((state) => ({
        projectsState: { ...state.projectsState, ...newState },
      })),
  };
};
